import { useQuery } from '@tanstack/react-query';
import MilestoneService from '../services/MilestoneService';

/**
 * Returns a project's due date as a YYYY-MM-DD string, or null.
 *
 * A project has no stored due date; it is the latest milestone's planned date
 * (the same value the schedule aggregate exposes as `lastPlannedDate`). This
 * hook fetches the project's milestones and returns the maximum planned date so
 * the top bar and info card stay in sync with the milestones themselves.
 */
const useProjectDueDate = (projectId?: string | null): string | null => {
  const { data } = useQuery<string | null>({
    queryKey: ['projectDueDate', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const response = await MilestoneService.getProjectMilestones(
        projectId as string,
        { limit: 200, page: 1 }
      );
      const milestones = response?.data?.milestones ?? [];

      let latest: string | null = null;
      for (const milestone of milestones) {
        const planned = milestone.plannedDate ?? null;
        // Planned dates share the YYYY-MM-DD prefix, so lexicographic compare
        // orders them chronologically regardless of any time component.
        if (planned && (!latest || planned > latest)) {
          latest = planned;
        }
      }

      return latest ? latest.slice(0, 10) : null;
    },
    staleTime: 30 * 1000,
  });

  return data ?? null;
};

export default useProjectDueDate;
